/**
 * PII scrub configuration shared by both Rollbar providers.
 *
 * Two complementary layers (factored into `@focal-point/pm-core` by #4244
 * so the chat write path and the Rollbar transform share one source of
 * truth):
 *
 * 1. `PII_SCRUB_FIELDS` is passed to Rollbar's `scrubFields` config, which
 *    filters Rollbar's standard request/body fields. Re-exported from
 *    pm-core (the same array, plus the financial/license keys #4244 added
 *    for chat).
 *
 * 2. `redactRollbarPayload` runs inside Rollbar's `transform` hook to:
 *    - Walk `data.custom` and `body.{message,trace,trace_chain[*]}.extra`
 *      and key-name-redact known PII keys (recursive `redactPII`). This
 *      catches caller-provided `extra` / `tags` payloads that Rollbar's
 *      scrubFields does not see.
 *    - Content-redact PII shapes (email, US/E.164 phone, SSN, Luhn-valid
 *      card number) inside the free-form text fields where PII can land
 *      naturally: `body.message.body`, every string in
 *      `body.trace.frames[*].args`, and the query string of
 *      `body.request.url`. See #3644 (M-1 audit follow-up).
 *
 * Audit finding M-1 (docs/product-knowledge/security/audit-2026-04-29-2881.md)
 * + #3644 outbound exposure audit. Refactored to consume pm-core
 * primitives in #4244 so the AI chat write path applies the SAME redaction
 * the Rollbar transform does, without two divergent regex copies.
 */

import { PII_SCRUB_FIELDS as PII_SCRUB_FIELDS_PM_CORE, redactPII as redactPiiPmCore, scrubPiiInString as scrubPiiInStringPmCore } from '@focal-point/pm-core/ai';

export const PII_SCRUB_FIELDS = PII_SCRUB_FIELDS_PM_CORE;
export const redactPII = redactPiiPmCore;
/**
 * Re-exported from pm-core (#4244). Used by the Rollbar transform here
 * and by tests in `rollbar.test.ts`. Keep the symbol exported -- the
 * downstream tests rely on it.
 */
export const scrubPiiInString = scrubPiiInStringPmCore;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Walk a `request.url` and scrub PII shapes inside its query string. Path
 * segments are left alone (they should not carry PII; if they do, that is a
 * separate routing-design bug worth surfacing in Rollbar so we can find it).
 *
 * Returns the input unchanged on parse failure.
 */
function scrubUrlQueryString(rawUrl: string): string {
  if (typeof rawUrl !== 'string' || rawUrl.length === 0) return rawUrl;
  const queryStart = rawUrl.indexOf('?');
  if (queryStart === -1) return rawUrl;
  const path = rawUrl.slice(0, queryStart);
  const query = rawUrl.slice(queryStart + 1);
  const fragmentStart = query.indexOf('#');
  const queryOnly = fragmentStart === -1 ? query : query.slice(0, fragmentStart);
  const fragment = fragmentStart === -1 ? '' : query.slice(fragmentStart);
  const scrubbedQuery = queryOnly
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq === -1) return pair;
      const key = pair.slice(0, eq);
      const value = pair.slice(eq + 1);
      if (value.length === 0) return pair;
      let decoded: string;
      try {
        decoded = decodeURIComponent(value);
      } catch {
        decoded = value;
      }
      const scrubbed = scrubPiiInString(decoded);
      return scrubbed === decoded ? pair : `${key}=${encodeURIComponent(scrubbed)}`;
    })
    .join('&');
  return `${path}?${scrubbedQuery}${fragment}`;
}

/**
 * Rollbar `transform` hook. Mutates `data` in place per Rollbar's contract.
 *
 * Walks two classes of subtree:
 *
 * - Caller-provided dictionaries (`custom`, `body.message.extra`,
 *   `body.trace.extra`, `body.trace_chain[*].extra`): key-name redaction
 *   via `redactPII`.
 * - Free-form text fields (`body.message.body`, every string in
 *   `body.trace.frames[*].args`, every string in
 *   `body.trace_chain[*].frames[*].args`, `body.request.url` query string,
 *   the top-level `data.title` override): content regex via `scrubPiiInString` /
 *   `scrubUrlQueryString`.
 */
/**
 * Content-redact the `message` of every link in `custom.causeChain` (#9959).
 *
 * `redactPII` on `custom` is KEY-NAME redaction, and `message` is not a PII key
 * name -- so without this, a driver error that happened to interpolate an email
 * or a phone number into its text would reach Rollbar verbatim. The cause chain
 * is a free-form text field exactly like `body.message.body` and the custom
 * `data.title`, so it gets the same treatment, in the same place, per the
 * one-location rule this module has held since #4244.
 *
 * Runs AFTER `redactPII` so it operates on the returned (possibly rebuilt)
 * object rather than the pre-redaction one.
 */
function scrubCauseChainMessages(custom: unknown): void {
  if (!isPlainObject(custom)) return;
  const chain = custom.causeChain;
  if (!Array.isArray(chain)) return;
  for (const link of chain) {
    if (isPlainObject(link) && typeof link.message === 'string') {
      link.message = scrubPiiInString(link.message);
    }
  }
}

export function redactRollbarPayload(data: Record<string, unknown>): void {
  if (!isPlainObject(data)) return;

  if (isPlainObject(data.custom)) {
    data.custom = redactPII(data.custom) as Record<string, unknown>;
    scrubCauseChainMessages(data.custom);
  }

  // The custom title override (set by applyStableFingerprint, #5454) is a
  // free-form text field that bypasses Rollbar's scrubFields, so content-redact
  // it the same way as body.message.body. Harmless no-op when title is unset.
  if (typeof data.title === 'string') {
    data.title = scrubPiiInString(data.title);
  }

  const body = data.body;
  if (isPlainObject(body)) {
    const message = body.message;
    if (isPlainObject(message)) {
      if (typeof message.body === 'string') {
        message.body = scrubPiiInString(message.body);
      }
      if (isPlainObject(message.extra)) {
        message.extra = redactPII(message.extra) as Record<string, unknown>;
      }
    }

    const trace = body.trace;
    if (isPlainObject(trace)) {
      if (isPlainObject(trace.extra)) {
        trace.extra = redactPII(trace.extra) as Record<string, unknown>;
      }
      scrubFrameArgs(trace.frames);
    }

    const traceChain = body.trace_chain;
    if (Array.isArray(traceChain)) {
      for (const frame of traceChain) {
        if (isPlainObject(frame)) {
          if (isPlainObject(frame.extra)) {
            frame.extra = redactPII(frame.extra) as Record<string, unknown>;
          }
          scrubFrameArgs(frame.frames);
        }
      }
    }

    const request = body.request;
    if (isPlainObject(request) && typeof request.url === 'string') {
      request.url = scrubUrlQueryString(request.url);
    }
  }
}

/**
 * Walk `frames[*].args` and scrub every string member in place. `args` is a
 * heterogeneous array per Rollbar's payload spec (each entry is the value
 * passed at the corresponding stack-frame call). We only touch strings; other
 * types are left alone so structured logs and numbers survive.
 */
function scrubFrameArgs(frames: unknown): void {
  if (!Array.isArray(frames)) return;
  for (const frame of frames) {
    if (!isPlainObject(frame)) continue;
    const args = frame.args;
    if (!Array.isArray(args)) continue;
    for (let i = 0; i < args.length; i++) {
      const a = args[i];
      if (typeof a === 'string') {
        args[i] = scrubPiiInString(a);
      }
    }
  }
}
